"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

/**
 * `useHoverCapable` — detects whether the current device has a
 * primary pointer that supports hover. On iOS/Android this
 * resolves `false`; on desktop + touch-enabled laptops it
 * resolves `true`. Used to swap Radix's hover-only tooltip into
 * a tap-to-toggle flow on touch-only devices so the lobby's
 * info bubbles are reachable without cursors.
 */
function useHoverCapable(): boolean {
  const [hoverCapable, setHoverCapable] = React.useState(true);
  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(hover: hover)");
    const handler = () => setHoverCapable(mq.matches);
    handler();
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return hoverCapable;
}

/**
 * Core Radix trigger passthrough. Re-exported as a named slot so
 * the smart `Tooltip` below can walk its children and clone the
 * trigger with an `onClick` toggle without breaking any existing
 * callers.
 */
const TooltipTrigger = TooltipPrimitive.Trigger

interface SmartTooltipProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> {
  /** Make the tooltip a desktop-hover-only affordance: render the
   *  trigger naked on touch-only devices (no Radix wrapper, no focus
   *  / pointer handlers attached). Useful when the trigger is
   *  already an interactive control (`<button>` with its own
   *  popover / navigation handler) and the tooltip is a "hover for
   *  more info" hint that has no place on phones — leaving Radix
   *  in the tree on touch caused a single-tap → tooltip-open,
   *  second-tap → click double-tap regression on iOS Safari (see
   *  comment on `skipTooltipOnTouch` below). */
  disableTouch?: boolean;
}

/**
 * Default export `Tooltip` — Radix's `Root` wrapped with a
 * tap-to-toggle fallback on touch-only devices. Hover devices
 * still get the Radix default behaviour verbatim, so existing
 * desktop UX is unchanged.
 *
 * Implementation detail: the touch path uses controlled state
 * (`open` + `onOpenChange`) and clones the first child of
 * `TooltipTrigger` with an `onClick` that toggles it. Clicks on
 * the portal content close via `onPointerDownOutside` (Radix
 * default), so tapping anywhere off the trigger dismisses it.
 * Callers who already supplied `open`/`onOpenChange` escape the
 * touch branch untouched — this is additive for uncontrolled
 * tooltips only.
 */
function Tooltip({
  children,
  disableTouch,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...props
}: SmartTooltipProps) {
  const hoverCapable = useHoverCapable();
  const [internalOpen, setInternalOpen] = React.useState(false);

  // Always pass a defined `open` to `Radix.Root` so the primitive
  // never observes a controlled→uncontrolled switch when
  // `useHoverCapable` updates from its hydration default (`true`) to
  // the real `matchMedia` value on the first effect tick. Previously
  // the component selected one of two `<Root>` shapes — uncontrolled
  // (`open={undefined}`) on hover devices vs controlled
  // (`open={uncontrolledOpen}`) on touch devices — and the brief
  // window between mount and effect resolution caused 100+
  // "Tooltip is changing from uncontrolled to controlled" warnings
  // per render cycle on every audit.
  const isExternallyControlled = controlledOpen !== undefined;
  const open = controlledOpen ?? internalOpen;
  const setOpen = React.useCallback(
    (v: boolean) => {
      if (!isExternallyControlled) setInternalOpen(v);
      controlledOnOpenChange?.(v);
    },
    [isExternallyControlled, controlledOnOpenChange],
  );

  // `disableTouch` callers (e.g. `BoardCard`, `BoardroomCard`) want the
  // hover-only affordance for desktop and never on touch — the trigger
  // is already an interactive `<button>` that handles its own tap.
  // Wrapping that button in a Radix tooltip on iOS introduced a
  // double-tap regression: Radix's `TooltipTrigger` attaches an
  // `onFocus` handler that calls `context.onOpen()` whenever the
  // button gains focus without an active pointer-down. iOS Safari
  // focuses a `<button>` after the synthesized tap completes, by
  // which point Radix's pointer-up listener has already cleared
  // `isPointerDownRef`, so the tooltip transitions to the open state
  // on the very first tap. The browser's "scroll/tap intent
  // disambiguation" then defers the synthesized `click` to a second
  // tap, leaving the user staring at a blue-highlighted card that
  // seemingly ignored their first press.
  //
  // Skipping the Radix wrapper entirely on touch-only devices removes
  // every focus / pointer handler the primitive would have attached,
  // so the underlying `<button>`'s own `onClick` fires on the first
  // tap. `!isExternallyControlled` guards external callers who
  // want to drive open state themselves (they keep Radix). Hover-
  // capable devices (desktop, hybrid laptops) keep the desktop
  // tooltip behaviour unchanged.
  const skipTooltipOnTouch =
    disableTouch === true && !hoverCapable && !isExternallyControlled;

  if (skipTooltipOnTouch) {
    let triggerContent: React.ReactNode = null;
    React.Children.forEach(children, (child) => {
      if (
        React.isValidElement(child) &&
        (child as React.ReactElement).type === TooltipTrigger
      ) {
        const triggerProps = (child as React.ReactElement).props as {
          children?: React.ReactNode;
        };
        triggerContent = triggerProps.children;
      }
    });
    return <>{triggerContent}</>;
  }

  // On touch-only devices for default tooltips, clone the trigger to
  // toggle on tap. On hover devices Radix's own pointer/focus handlers
  // drive the open state via `onOpenChange`; we never short-circuit
  // those paths, we only *augment* the trigger with an additional
  // `onClick` toggle when needed.
  const useTouchBranch =
    !disableTouch && !hoverCapable && !isExternallyControlled;

  return (
    <TooltipPrimitive.Root open={open} onOpenChange={setOpen} {...props}>
      {useTouchBranch
        ? React.Children.map(children, (child) => {
            if (
              React.isValidElement<{ onClick?: React.MouseEventHandler }>(
                child,
              ) &&
              (child as React.ReactElement).type === TooltipTrigger
            ) {
              return React.cloneElement(child, {
                onClick: (e: React.MouseEvent) => {
                  child.props.onClick?.(e);
                  setOpen(!open);
                },
              });
            }
            return child;
          })
        : children}
    </TooltipPrimitive.Root>
  );
}

/**
 * Backwards-compatible named export. `TouchTooltip` was introduced
 * as an opt-in touch-aware wrapper; now that the default `Tooltip`
 * is touch-aware itself, this alias keeps existing call sites
 * functioning without a codemod.
 */
const TouchTooltip = Tooltip;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TouchTooltip }
